import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ConsoleSession } from "@/lib/types";
import { Panel, SectionHead } from "@/ui/Panel";
import { Button } from "@/ui/Button";
import { Chip, type ChipTone } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { ErrorState, TableSkeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { absoluteTime, cx, relativeTime } from "@/lib/format";
import { useService, serviceBase } from "@/features/services/useService";
import { ConsoleTerminal } from "@/features/console/Terminal";

export const Route = createFileRoute(
  "/_app/o/$orgId/projects/$projectId/services/$serviceId/console",
)({
  component: ConsoleScreen,
});

const STATE_LABEL: Record<
  ConsoleSession["state"],
  { label: string; tone: ChipTone | undefined }
> = {
  pending: { label: "Waiting", tone: "live" },
  attached: { label: "Open", tone: "live" },
  closed: { label: "Ended", tone: undefined },
  failed: { label: "Failed", tone: "down" },
  expired: { label: "Timed out", tone: "warn" },
};

/**
 * A shell inside a running copy of this service.
 *
 * # THE SHAPE OF THE SCREEN
 *
 * Two things, in the order they matter: the way in, and the record of
 * everybody who has gone in before. The record is not an afterthought on this
 * screen — a console session reads every secret the service holds, so who
 * opened one is as much the point of the page as opening one is.
 *
 * # WHY THE WAY IN IS NOT A PANEL WITH A BUTTON IN IT
 *
 * Because it is a decision with a consequence, and the consequence is the
 * thing to lead with. What it reads now is: what you are about to get, what
 * it can see, that it is written down, and then the button. In that order,
 * because somebody who reads only the first line should still have been told
 * the important part.
 */
function ConsoleScreen() {
  const { orgId, projectId, serviceId } = Route.useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [session, setSession] = useState<ConsoleSession | null>(null);

  const service = useService(orgId, projectId, serviceId);
  const base = serviceBase(orgId, projectId, serviceId);

  const history = useQuery({
    queryKey: ["console-sessions", serviceId],
    queryFn: ({ signal }) =>
      api.get<{ sessions: ConsoleSession[] | null }>(`${base}/console`, { limit: 20 }, signal),
  });

  const open = useMutation({
    mutationFn: () => api.post<{ session: ConsoleSession }>(`${base}/console`, {}),
    onSuccess: async ({ session: opened }) => {
      setSession(opened);
      await queryClient.invalidateQueries({ queryKey: ["console-sessions", serviceId] });
    },
    onError: toast.error,
  });

  // Stable, so the terminal's effect does not tear itself down on every render
  // of this screen — which would close the session the moment anything else
  // changed.
  const onClosed = useCallback(
    (reason: string) => {
      toast.show({ tone: "info", title: reason });
      void queryClient.invalidateQueries({ queryKey: ["console-sessions", serviceId] });
    },
    [toast, queryClient, serviceId],
  );

  const svc = service.data?.service;
  const running = svc?.state === "RUNNING" || svc?.state === "DEGRADED";
  const sessions = history.data?.sessions ?? [];

  return (
    <div className="flex flex-col gap-8">
      {session ? (
        <ConsoleTerminal
          session={session}
          onClosed={onClosed}
          onEnd={() => setSession(null)}
          onReopen={() => open.mutate()}
          reopening={open.isPending}
        />
      ) : (
        <section
          className={cx(
            "card flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:gap-8",
          )}
        >
          <div className="min-w-0 flex-1">
            <h2 className="t-sub">Open a shell</h2>
            <p className="t-body mt-1.5 max-w-[62ch]">
              You get a prompt inside one of the running copies of this
              service, with its environment, its files and its data. It can
              read every setting and secret the service has.
            </p>
            <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1">
              <Fact>Recorded in the activity log</Fact>
              <Fact>Closes itself after thirty minutes</Fact>
              <Fact>Cannot be resumed once it ends</Fact>
            </ul>
          </div>

          <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
            <Button
              tone="primary"
              busy={open.isPending}
              disabled={!running}
              onClick={() => open.mutate()}
            >
              Open a session
            </Button>
            {!running && (
              <p className="t-meta max-w-[28ch] sm:text-right">
                {svc?.state_reason ??
                  "This service is not running, so there is nothing to open a session in."}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="flex min-w-0 flex-col gap-3.5">
        <SectionHead
          title="Who has opened one"
          description="Every session, whether it opened or not. This list is the reason the feature is safe to have."
        />
        <Panel padded={false}>
          {history.isLoading ? (
            <TableSkeleton rows={3} columns={4} />
          ) : history.isError ? (
            <div className="p-5">
              <ErrorState error={history.error} onRetry={() => void history.refetch()} />
            </div>
          ) : sessions.length === 0 ? (
            <p className="t-body px-5 py-8">
              Nobody has opened one. Sessions appear here the moment they are
              asked for, including the ones that are refused.
            </p>
          ) : (
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[30%]">Opened</Th>
                  <Th width="w-[24%]">Ran</Th>
                  <Th width="w-[30%]">What happened</Th>
                  <Th width="w-[16%]" align="right">Ended</Th>
                </Tr>
              </thead>
              <tbody>
                {sessions.map((entry) => (
                  <Tr key={entry.id}>
                    <Td
                      clip
                      title={absoluteTime(entry.created_at)}
                      secondary={entry.requested_by_email}
                    >
                      {relativeTime(entry.created_at)}
                    </Td>
                    <Td clip>
                      <span className="mono">{entry.command.join(" ")}</span>
                    </Td>
                    <Td clip title={entry.reason ?? undefined} secondary={entry.reason}>
                      <Chip tone={STATE_LABEL[entry.state].tone}>
                        {STATE_LABEL[entry.state].label}
                      </Chip>
                    </Td>
                    <Td align="right" clip title={absoluteTime(entry.closed_at)}>
                      {entry.closed_at ? relativeTime(entry.closed_at) : "—"}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      </section>
    </div>
  );
}

/** One thing worth knowing before opening a shell in production. */
function Fact({ children }: { children: React.ReactNode }) {
  return (
    <li className="t-meta flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className="size-1 shrink-0 rounded-full bg-[var(--text-muted)]"
      />
      {children}
    </li>
  );
}
